import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState, classes } from '@web/components/ui.tsx';
import { CHART_SERIES, ChartLegend, WeeklyTrendChart } from '@web/components/reports/charts.tsx';
import { formatMinutes } from '@web/i18n/format.ts';
import {
  reportRetry,
  formatDay,
  useReportDate,
  StatTile,
  ReportBoundary,
  SectionCard,
  TH,
  THR,
  TD,
  TDR,
  ROW,
  type RangeParams,
} from '../shared.tsx';

export function LeadsSection({ range, scope }: { range: RangeParams; scope?: 'all' }) {
  const formatDate = useReportDate();
  const { t } = useTranslation();
  const paging = useReportPaging({ ...range, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'leads', { ...range, scope, pages }, paging.identity],
    queryFn: () => reportsApi.leads({ ...range, scope, pages }),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        const hours = (value: number | null) => (value === null ? '—' : formatMinutes(Math.round(value * 60)));
        const trendSeries = [
          {
            name: t('reports.leads.seriesReceived'),
            color: CHART_SERIES[0],
            values: report.weekly.map((week) => week.received),
          },
          {
            name: t('reports.leads.seriesConverted'),
            color: CHART_SERIES[1],
            values: report.weekly.map((week) => week.converted),
          },
        ];

        // The untouched list is today's to-do across all dates — an old lead
        // going cold must show even when the selected range saw no arrivals.
        if (
          report.counts.total === 0 &&
          report.untouched.length === 0 &&
          (report.pagination?.untouched?.page ?? 1) === 1
        ) {
          return <EmptyState title={t('reports.leads.emptyTitle')}>{t('reports.leads.emptyBody')}</EmptyState>;
        }

        return (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label={t('reports.leads.received')}
                value={String(report.counts.total)}
                hint={t('reports.leads.receivedHint', {
                  converted: report.counts.converted,
                  dismissed: report.counts.dismissed,
                })}
              />
              <StatTile
                label={t('reports.leads.conversion')}
                value={report.conversionRatePercent === null ? '—' : `${report.conversionRatePercent}%`}
                hint={t('reports.leads.conversionHint')}
              />
              <StatTile
                label={t('reports.leads.firstTouch')}
                value={hours(report.medianHoursToFirstTouch)}
                hint={t('reports.leads.firstTouchHint', { time: hours(report.avgHoursToFirstTouch) })}
              />
              <StatTile
                label={t('reports.leads.untouchedNow')}
                value={String(report.counts.new)}
                hint={t('reports.leads.untouchedNowHint')}
              />
            </div>

            <div className="space-y-6">
              {report.weekly.length >= 2 ? (
                <SectionCard
                  title={t('reports.leads.trendTitle')}
                  hint={t('reports.leads.trendHint')}
                  csvHref={reportsApi.csvUrl('leads', { ...range, scope })}
                >
                  <ChartLegend items={trendSeries.map(({ name, color }) => ({ name, color }))} />
                  <WeeklyTrendChart
                    labels={report.weekly.map((week) => formatDay(week.weekStart))}
                    series={trendSeries}
                    ariaLabel={t('reports.leads.trendTitle')}
                  />
                </SectionCard>
              ) : null}

              <SectionCard
                title={t('reports.leads.untouchedTitle')}
                collection="untouched"
                hint={t('reports.leads.untouchedHint')}
              >
                {report.untouched.length === 0 ? (
                  <p className="text-[color:var(--color-muted)]">{t('reports.leads.untouchedEmpty')}</p>
                ) : (
                  <TableScroll aria-label={t('reports.leads.untouchedTitle')} className="overflow-x-auto">
                    <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                      <thead>
                        <tr className="border-b border-[color:var(--color-line)]">
                          <th scope="col" className={TH}>
                            {t('reports.leads.colName')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.leads.colReceived')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.leads.colWaiting')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.untouched.map((row) => (
                          <tr key={row.id} className={ROW}>
                            <td className={classes(TD, 'table-text max-w-[18rem]')}>
                              {/* The leads inbox lists them all — there is no per-lead page. */}
                              <Link to="/requests" className="text-[color:var(--color-brand)] hover:underline">
                                {row.name}
                              </Link>
                            </td>
                            <td className={TDR}>{formatDate(row.createdAt)}</td>
                            <td className={TDR}>
                              {formatMinutes(Math.max(0, Math.round((Date.now() - Date.parse(row.createdAt)) / 60000)))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                )}
              </SectionCard>
            </div>
          </>
        );
      }}
    </ReportBoundary>
  );
}
